import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  model,
  Output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import {
  DateFormat,
  DatePipe,
  DefaultTimeStringParseMethod,
  DestroySubscribers,
  IconComponent,
  ModalComponent,
  ModalService,
  TimeStringParser,
} from '@ftr/foundation'
import { InlineInputComponent, InlineLocalTimeInputComponent, InlineSaveStatusComponent } from '@ftr/inline-forms'
import { BehaviorSubject, combineLatest, debounceTime, map, skip, takeUntil } from 'rxjs'
import { HearingSectionModel } from '../../../store'
import { DeleteHearingAnnotationModalComponent } from '../delete-hearing-annotation-modal'

export interface HearingSectionFormModel {
  startTime: string
  endTime: string
  caseReference: string
  recordedCaseTitle: string
}

@Component({
  selector: 'ftr-hearing-section-annotation-form',
  templateUrl: './hearing-section-annotation-form.component.html',
  styleUrls: ['./hearing-section-annotation-form.component.css'],
  imports: [
    CommonModule,
    InlineInputComponent,
    InlineLocalTimeInputComponent,
    InlineSaveStatusComponent,
    IconComponent,
    ModalComponent,
    DeleteHearingAnnotationModalComponent,
    DatePipe,
  ],
  standalone: true,
})
export class HearingSectionAnnotationFormComponent extends DestroySubscribers {
  value = model.required<HearingSectionModel>()
  caseReferenceInput = viewChild.required<InlineInputComponent>('caseReferenceInput')
  readOnly = input(false)
  status = computed(() => this.value().status)

  protected endTimeInputFocused = signal(false)
  protected endTimeMenuFocused = signal(false)
  protected endTimeMenuIsOpen = computed(() => {
    const menuFocused = this.endTimeMenuFocused()
    const inputFocused = this.endTimeInputFocused()
    return (menuFocused || inputFocused) && this.value().showSetToOngoingButton
  })
  protected retry$ = new BehaviorSubject<void>(undefined)

  private autofocus = computed(() => this.value().autofocus)

  // Allows the parent to clear local state
  private forceResetForm = computed(() => this.value().forceResetFormKey)

  // Reflects what's been changed in the UI - we need to keep track because the store does not remember invalid values
  protected uiHearingSectionModel = signal<HearingSectionFormModel>(
    {
      caseReference: '',
      recordedCaseTitle: '',
      startTime: '',
      endTime: '',
    },
    { equal: simpleObjectEquals },
  )

  private mappedFormValue = computed<HearingSectionFormModel>(
    () => {
      const value = this.value()
      return {
        caseReference: value.caseReference ?? '',
        recordedCaseTitle: value.recordedCaseTitle ?? '',
        startTime: new DatePipe().transform(value.startTime, { format: DateFormat.TimeWithSeconds }),
        endTime: value.showAsOngoing
          ? ''
          : new DatePipe().transform(value.endTime, { format: DateFormat.TimeWithSeconds }),
      }
    },
    { equal: simpleObjectEquals },
  )

  @Output()
  public onValueChanged = new EventEmitter<HearingSectionFormModel>()

  @Output()
  public onHearingDeleted = new EventEmitter<void>()

  @Output()
  public onSetHearingToOngoing = new EventEmitter<void>()

  protected dateFormat = DateFormat
  protected timeInputPlaceholder = 'H:MM:SS AM'

  // Presentation component should not have any dependencies injected, but formbuilder is pretty safe
  // eslint-disable-next-line local-rules/no-constructor
  constructor(
    private readonly modalService: ModalService,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    super()

    effect(() => {
      const caseReferenceInput = this.caseReferenceInput()
      if (this.autofocus()) {
        caseReferenceInput.focus()
      }
    })

    // After the user has entered the values, we need to format them (especially for time inputs)
    effect(() => {
      this.uiHearingSectionModel()
      // we defer the formatting to the next tick to ensure the updates are actually rendered
      setTimeout(() => this.formatInputs())
    })

    // Allow the parent to continually propagate change into local state
    effect(
      () => {
        this.forceResetForm()
        this.uiHearingSectionModel.set(this.mappedFormValue())
      },
      {
        allowSignalWrites: true,
      },
    )

    // We use an observable over the local state mainly for Rx's debounce so that we can avoid emitting too many changes
    // especially at times were we are formatting the inputs after the user has entered them
    const uiHearingSectionModel$ = toObservable(this.uiHearingSectionModel)

    combineLatest([uiHearingSectionModel$, this.retry$])
      .pipe(
        // skip emitting the initial value which is written when initialising the component
        skip(1),
        debounceTime(50),

        // workaround for plumbing through legal endTimes when ongoing
        map(([formModel]) => {
          return {
            ...formModel,
            endTime:
              // if the hearing section is ongoing, and the form field is untouched, we'll just pass the true underlying
              // endTime through - otherwise we'll pass the model value.
              this.value().showAsOngoing && formModel.endTime === ''
                ? this.value().endTime.toString()
                : formModel.endTime,
          }
        }),
        takeUntil(this.finalize),
      )
      .subscribe(changes => {
        this.onValueChanged.emit(changes)
      })
  }

  protected onChange(value: Partial<HearingSectionFormModel>): void {
    this.uiHearingSectionModel.set({
      ...this.uiHearingSectionModel(),
      ...value,
    })
  }

  protected onUndo(): void {
    this.uiHearingSectionModel.set(this.mappedFormValue())
  }

  protected openModal(ref: TemplateRef<unknown>): void {
    this.modalService.open(ref, this.viewContainerRef)
  }

  protected hasError(prop: keyof HearingSectionModel): boolean {
    const status = this.status()
    return (typeof status === 'object' && status.fields?.includes(prop)) || false
  }

  protected handleEndTimeInputBlur(): void {
    // setTimeout is needed to prevent the menu from closing before the button can be focussed
    setTimeout(() => {
      this.endTimeInputFocused.set(false)
    })
  }

  protected handleSetToOngoing(): void {
    this.endTimeMenuFocused.set(false)
    this.onSetHearingToOngoing.emit()
  }

  private formatInputs(): void {
    const current = this.uiHearingSectionModel()
    this.uiHearingSectionModel.set({
      caseReference: current.caseReference.trim(),
      recordedCaseTitle: current.recordedCaseTitle.trim(),
      startTime: this.tryFormatTime(current.startTime).trim() || this.mappedFormValue().startTime,
      endTime: this.tryFormatTime(current.endTime) || this.mappedFormValue().endTime,
    })
  }

  private tryFormatTime(timeInput: string): string {
    try {
      const localTime = TimeStringParser.parse(timeInput.trim(), [DefaultTimeStringParseMethod])
      return new DatePipe().transform(localTime, { format: DateFormat.TimeWithSeconds })
    } catch {
      return timeInput
    }
  }
}

function simpleObjectEquals<T>(a: T, b: T): boolean {
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false
  }

  const objectA = a as Record<string, unknown>
  const objectB = b as Record<string, unknown>
  const aKeys = Object.keys(objectA)
  const bKeys = Object.keys(objectB)
  return aKeys.length === bKeys.length && aKeys.every(key => objectA[key] === objectB[key])
}
